/**
 * TypingIndicator — chat-row "Atlas is typing" cue.
 *
 * Drop inside a MessageRow placeholder while the assistant is composing.
 * Two variants:
 *   - `dots`  (default) — three dots that arrive in sequence; the universal
 *                         chat convention (iMessage, WhatsApp, Slack).
 *   - `aiko`            — breathing AikoMark; reinforces brand presence on
 *                         AIKO-led surfaces (AikoChat panel, hero greeters).
 *
 * For `prefers-reduced-motion` users (and `motion={false}`), both variants
 * render their resting frame so the indicator still reads as "active" via
 * `role="status"` + `aria-label`, but no continuous animation runs.
 *
 * For longer-running platform AI work that isn't a chat row (initial load,
 * generation), reach for `<AikoLoader>` instead — TypingIndicator is sized
 * to live inline next to or inside a message bubble.
 */
import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../lib/cn";
import { useReducedMotion } from "../hooks/useReducedMotion";
import { AikoMark, type AikoMarkProps } from "./AikoMark";

/* Component-scoped keyframes (the house pattern: ListeningOverlay, AikoSurfaces,
   ConnectorOnboarding). CSS, deliberately, NOT `motion`.

   The dots used to be `motion.span`s bouncing on a y keyframe, which is the
   RIGHT motion. It simply never ran: framer mounted the elements (it wrote
   `style="transform: none"`) and then never advanced them. Measured on staging
   AND locally at 234 consecutive samples, every one identical, so users saw
   three dead dots for the whole pre-token wait. Exactly the failure
   ListeningOverlay's own comment records ("animated bar heights off Date.now()
   in render, which never moved"), and the same cure: a real CSS animation the
   compositor owns, which cannot silently stall.

   So the intent is preserved and only the mechanism changes: each dot lifts 3px
   and brightens, 0.15s after the one before it, so the highlight travels
   1 -> 2 -> 3 and repeats. The stagger is a per-dot `animation-delay`, which
   means the browser keeps them in phase however long the wait runs. */
const TYPING_STYLE = `
@keyframes aisquare-typing-dot {
  0%, 55%, 100% { transform: translateY(0);    opacity: 0.45; }
  25%           { transform: translateY(-3px); opacity: 1; }
}
.aisquare-typing-dot {
  animation: aisquare-typing-dot 1.2s ease-in-out infinite;
  will-change: transform, opacity;
}
@media (prefers-reduced-motion: reduce) {
  .aisquare-typing-dot { animation: none; transform: none; opacity: 0.6; }
}
`;

/* ============================================================================
 * Sizing — kept off to the side so adding a fourth size is one map entry.
 * ========================================================================= */

type Size = "sm" | "default" | "lg";

const DOT_CLASS: Record<Size, string> = {
  sm: "h-1 w-1",
  default: "h-1.5 w-1.5",
  lg: "h-2 w-2",
};

const MARK_SIZE: Record<Size, NonNullable<AikoMarkProps["size"]>> = {
  sm: "xs",
  default: "sm",
  lg: "md",
};

/* The visible `label` sits on the dots' own baseline, so it tracks size with
   them rather than inheriting whatever the call site happens to be running. */
const LABEL_CLASS: Record<Size, string> = {
  sm: "text-[11px] leading-none",
  default: "text-[13px] leading-none",
  lg: "text-sm leading-none",
};

const indicatorVariants = cva("inline-flex items-center", {
  variants: {
    size: {
      sm: "gap-[3px]",
      default: "gap-1",
      lg: "gap-1.5",
    },
  },
  defaultVariants: { size: "default" },
});

/* ============================================================================
 * Component
 * ========================================================================= */

export interface TypingIndicatorProps
  extends
    Omit<React.HTMLAttributes<HTMLDivElement>, "children">,
    VariantProps<typeof indicatorVariants> {
  /**
   * Visual treatment.
   *   - `dots` — three dots arriving in sequence (default).
   *   - `aiko` — breathing AikoMark for AIKO-led surfaces.
   */
  variant?: "dots" | "aiko";
  /**
   * Disable continuous animation. Default true. `prefers-reduced-motion`
   * forces this off regardless of the prop, matching every other primitive
   * in this library.
   */
  motion?: boolean;
  /**
   * Screen-reader label for `role="status"`. Defaults to "Atlas is typing"
   * (Atlas voice, terse, no em-dash). Override per surface as needed.
   */
  "aria-label"?: string;
  /**
   * Optional VISIBLE word rendered before the dots, e.g. `"Thinking"`.
   *
   * Dots alone are ambiguous during a long pre-token wait: they read as "the
   * connection is alive" rather than "the assistant is working on your
   * question". A word removes that ambiguity, and it is what makes the wait
   * legible on surfaces where the model may think for many seconds before the
   * first token. Omitted by default, so every existing call site renders
   * exactly as it does today.
   *
   * Purely decorative for assistive tech: it is marked `aria-hidden` because
   * `aria-label` on the `role="status"` wrapper already announces the state,
   * and announcing both would say it twice.
   */
  label?: React.ReactNode;
}

export const TypingIndicator = React.forwardRef<HTMLDivElement, TypingIndicatorProps>(
  (
    {
      className,
      size,
      variant = "dots",
      motion: enableMotion = true,
      "aria-label": ariaLabel = "Atlas is typing",
      label,
      ...rest
    },
    ref,
  ) => {
    const reduced = useReducedMotion();
    const shouldAnimate = enableMotion && reduced !== true;
    const resolvedSize: Size = size ?? "default";

    return (
      <div
        ref={ref}
        role="status"
        aria-live="polite"
        aria-label={ariaLabel}
        className={cn(indicatorVariants({ size }), className)}
        {...rest}
      >
        {label != null && label !== "" && (
          <span aria-hidden className={cn(LABEL_CLASS[resolvedSize], "text-muted-foreground")}>
            {label}
          </span>
        )}
        {variant === "aiko" ? (
          <AikoMark size={MARK_SIZE[resolvedSize]} animated={shouldAnimate} />
        ) : (
          <Dots size={resolvedSize} animated={shouldAnimate} />
        )}
      </div>
    );
  },
);
TypingIndicator.displayName = "TypingIndicator";

/* ============================================================================
 * Internal — three dots that arrive in sequence
 *
 * Each dot lifts and brightens 0.15s after the one before it, so the highlight
 * travels 1 -> 2 -> 3 and then repeats. The stagger is a per-dot
 * `animation-delay`, so the browser owns the timing and the three stay in phase
 * no matter how long the wait runs.
 *
 * The resting frame is 0.45 opacity, never 0: all three dots stay legible
 * throughout, so the row reads as a single object with a travelling accent
 * rather than dots flickering in and out of existence.
 * ========================================================================= */

const DOT_KEYS = [0, 1, 2] as const;
const DOT_STAGGER_S = 0.15;

function Dots({ size, animated }: { size: Size; animated: boolean }) {
  /* The dot is a SOLID token and `opacity` does all the fading. The first cut of
     this kept the old `/60` fill and animated opacity on top of it, which
     multiplies: 0.6 x 0.28 is an effective 0.17 alpha, so the dots were
     invisible for most of every cycle and the indicator looked like it was not
     rendering at all. One alpha channel, one owner. */
  const klass = cn(DOT_CLASS[size], "rounded-full bg-muted-foreground");
  if (!animated) {
    /* The resting frame keeps the pre-existing /60 weight, since nothing is
       animating it back up. */
    return (
      <>
        {DOT_KEYS.map((i) => (
          <span
            key={i}
            aria-hidden
            className={cn(DOT_CLASS[size], "rounded-full bg-muted-foreground/60")}
          />
        ))}
      </>
    );
  }
  return (
    <>
      <style>{TYPING_STYLE}</style>
      {DOT_KEYS.map((i) => (
        <span
          key={i}
          aria-hidden
          className={cn(klass, "aisquare-typing-dot")}
          style={{ animationDelay: `${i * DOT_STAGGER_S}s` }}
        />
      ))}
    </>
  );
}
